import { useIntl } from 'react-intl';

import { useAppConfigContext } from '~/contexts/AppConfig';
import { Box } from '~/eds/src/components/Box/Box';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { MarkedText } from '~/eds/src/components/MarkedText/MarkedText';
import { CountrySelect } from '~/eds/src/components/Select/subcomponents/CountrySelect';
import { StateProvinceSelect } from '~/eds/src/components/Select/subcomponents/StateProvinceSelect';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { Nullable } from '~/types/Nullable';

import { WorkflowQuestionFormField } from './types';

export const AddressWorkflowQuestion = ({
  question,
  onChange,
  ...commonProps
}: WorkflowQuestionFormField<Nullable<string>>) => {
  const intl = useIntl();
  const { isPanelEnlarged } = useAppConfigContext();
  const {
    field: { value },
  } = question;
  const { id, label, description, search, error, ...rest } = commonProps;

  return (
    <Layout direction="column" spacing={2}>
      <Layout direction="column">
        <Label>
          <MarkedText search={search} text={label} />
        </Label>
        {description && (
          <Text preset="description">
            <MarkedText search={search} text={description} />
          </Text>
        )}
      </Layout>
      <FormField
        {...rest}
        id={`${id}-country`}
        name={`${id}-country`}
        search={search}
        label={intl.formatMessage({
          id: 'AddressWorkflowQuestion.countryLabel',
          defaultMessage: 'Country',
          description: 'Label for country field in address form',
        })}
        input={CountrySelect}
        value={value.country}
        error={!value.country ? error : undefined}
        onChange={(val: Nullable<string>) => onChange(val, 'country')}
        inputProps={{
          isMulti: false,
          enablePortal: true,
          width: isPanelEnlarged ? 'input.l.width' : '100%',
        }}
      />
      <FormField
        {...rest}
        id={`${id}-street-1`}
        name={`${id}-street-1`}
        search={search}
        label={intl.formatMessage({
          id: 'AddressWorkflowQuestion.streetAddress1Label',
          defaultMessage: 'Street Address 1',
          description: 'Label for first street address field in address form',
        })}
        input={TextInput}
        value={value.street_1}
        error={!value.street_1 ? error : undefined}
        onChange={(val: string) => onChange(val, 'street_1')}
        inputProps={{
          width: isPanelEnlarged ? 'input.l.width' : '100%',
        }}
      />
      <FormField
        {...rest}
        required={false}
        id={`${id}-street-2`}
        name={`${id}-street-2`}
        search={search}
        label={intl.formatMessage({
          id: 'AddressWorkflowQuestion.streetAddress2Label',
          defaultMessage: 'Street Address 2',
          description: 'Label for second street address field in address form',
        })}
        input={TextInput}
        value={value.street_2}
        onChange={(val: string) => onChange(val, 'street_2')}
        inputProps={{
          width: isPanelEnlarged ? 'input.l.width' : '100%',
        }}
      />
      <Layout direction={isPanelEnlarged ? 'row' : 'column'} spacing={2}>
        <Box flex={isPanelEnlarged ? '0 0 auto' : undefined}>
          <FormField
            {...rest}
            id={`${id}-city`}
            name={`${id}-city`}
            search={search}
            label={intl.formatMessage({
              id: 'AddressWorkflowQuestion.cityLabel',
              defaultMessage: 'City',
              description: 'Label for city field in address form',
            })}
            input={TextInput}
            value={value.city}
            error={!value.city ? error : undefined}
            onChange={(val: string) => onChange(val, 'city')}
            inputProps={{
              width: isPanelEnlarged ? 'input.m.width' : '100%',
            }}
          />
        </Box>
        <Box flex={isPanelEnlarged ? '0 0 auto' : undefined}>
          <FormField
            {...rest}
            id={`${id}-state`}
            name={`${id}-state`}
            search={search}
            label={intl.formatMessage({
              id: 'AddressWorkflowQuestion.stateProvinceLabel',
              defaultMessage: 'State / Province',
              description: 'Label for state or province field in address form',
            })}
            input={StateProvinceSelect}
            value={value.state || null}
            error={!value.state ? error : undefined}
            onChange={(val: Nullable<string>) => onChange(val, 'state')}
            inputProps={{
              isMulti: false,
              enablePortal: true,
              width: isPanelEnlarged ? 'input.m.width' : '100%',
              country: value.country,
            }}
          />
        </Box>
      </Layout>
      <FormField
        {...rest}
        id={`${id}-zip-code`}
        name={`${id}-zip-code`}
        search={search}
        label={intl.formatMessage({
          id: 'AddressWorkflowQuestion.zipCodePostCodeLabel',
          defaultMessage: 'Zip Code / Post Code',
          description: 'Label for zip code or post code field in address form',
        })}
        input={TextInput}
        value={value.zip_code}
        error={!value.zip_code ? error : undefined}
        onChange={(val: string) => onChange(val, 'zip_code')}
        inputProps={{
          width: isPanelEnlarged ? 'input.s.width' : '100%',
        }}
      />
    </Layout>
  );
};
